import React, { useEffect, useState } from "react";
import { useLocationStore } from "../../store/location.store";
import { useTheme } from "../../context/ThemeContext";
import { motion, AnimatePresence } from "framer-motion";

export const DynamicLocation: React.FC = () => {
  const { label, coords, setPromptOpen } = useLocationStore();
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const isCool = theme === 'cool';

  // Trigger expansion animation when location label changes (and isn't empty)
  useEffect(() => {
    if (label && coords) {
      setIsExpanded(true);
      const timer = setTimeout(() => setIsExpanded(false), 2500); // Expand for 2.5s on update
      return () => clearTimeout(timer);
    }
  }, [label, coords]);

  return (
    <motion.button
      onClick={() => setPromptOpen(true)}
      type="button"
      layout
      initial={false}
      animate={{
        width: isExpanded ? "auto" : "auto",
        borderRadius: 24,
      }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
      aria-label={coords ? `Location: ${label}` : "Set delivery location"}
      className={`relative min-h-[44px] h-9 sm:h-9 flex items-center justify-center overflow-hidden shadow-sm border backdrop-blur-md cursor-pointer transition-colors shrink-0 ${
        isExpanded
          ? (isCool ? "bg-cyan-900/90 border-cyan-500 text-cyan-50 px-3 sm:px-4" : "bg-orange-900/90 border-orange-500 text-white px-3 sm:px-4")
          : (isCool ? "bg-slate-800/50 border-cyan-900/30 text-cyan-400 hover:bg-slate-800 hover:border-cyan-500 px-2.5 sm:px-3" : "bg-white/60 border-orange-200 text-orange-800 hover:bg-white hover:border-orange-400 px-2.5 sm:px-3")
      }`}
    >
      <AnimatePresence mode="wait">
        {isExpanded ? (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
          >
            <span className="text-base sm:text-lg">📍</span>
            <div className="flex flex-col leading-none text-left">
              <span className="text-[8px] uppercase tracking-wider opacity-80 font-bold">Updated</span>
              <span className="text-xs font-bold truncate max-w-[100px] sm:max-w-[150px]">{label}</span>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="collapsed"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="flex items-center gap-1.5 sm:gap-2"
          >
            <span className="text-sm">📍</span>
            <span className={`text-xs font-bold max-w-[88px] sm:max-w-[120px] truncate ${isCool ? 'opacity-90' : 'opacity-100'}`}>
              {coords ? label : (
                <>
                  <span className="sm:hidden">Location</span>
                  <span className="hidden sm:inline">Set Location</span>
                </>
              )}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
};
